import {
  ArrowRight, Briefcase, CalendarClock, FileSignature, MailCheck, TrendingUp, UserPlus, Users,
} from 'lucide-react'
import { Link } from 'react-router'
import { Callout, ErrorState, PageHeader, StatCard } from '@/components/common'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useRecruiterDashboard } from '../api'

const NEXT_STEPS = [
  {
    to: '/recruitment/requisitions',
    label: 'Requisitions',
    description: 'Raise one, or see what the headcount budget said about the open ones.',
    icon: Briefcase,
  },
  {
    to: '/recruitment/candidates',
    label: 'Candidates',
    description: 'One record per person, however many jobs they apply to.',
    icon: Users,
  },
  {
    to: '/recruitment/offers',
    label: 'Offers',
    description: 'Reading one is recorded, the same as a payslip.',
    icon: FileSignature,
  },
]

/**
 * The recruiter's figures.
 *
 * When nothing is configured the zeros are explained rather than left bare: "0 open
 * requisitions" reads as a quiet month, when what it actually means is that nobody has raised
 * one yet. The two are different problems.
 */
export function RecruiterDashboardPage() {
  useBreadcrumb()
  const dashboard = useRecruiterDashboard()

  if (dashboard.isPending) return <Skeleton className="h-48" />
  if (dashboard.isError) {
    return <ErrorState error={dashboard.error} onRetry={() => void dashboard.refetch()} />
  }
  if (!dashboard.data) return null

  const data = dashboard.data

  return (
    <div className="space-y-5">
      <PageHeader
        title="Recruitment"
        subtitle="What is open, who is in play, and what is waiting on an answer."
      />

      {data.emptyReason && <Callout>{data.emptyReason}</Callout>}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <StatCard
          icon={Briefcase}
          label="Open requisitions"
          value={String(data.openRequisitions)}
          tint="purple"
        />
        <StatCard
          icon={TrendingUp}
          label="Over headcount budget"
          value={String(data.overBudgetRequisitions)}
          tint={data.overBudgetRequisitions > 0 ? 'orange' : 'green'}
        />
        <StatCard
          icon={Users}
          label="Candidates in play"
          value={String(data.activeCandidates)}
          tint="blue"
        />
        <StatCard
          icon={CalendarClock}
          label="Interviews scheduled"
          value={String(data.interviewsScheduled)}
          tint="blue"
        />
        <StatCard
          icon={MailCheck}
          label="Offers awaiting a reply"
          value={String(data.offersOutstanding)}
          tint="orange"
        />
        <StatCard icon={UserPlus}
          label="Hired this month" value={String(data.hiresThisMonth)} tint="green" />
      </div>

      <section aria-labelledby="next-steps">
        <h2 id="next-steps" className="mb-3 text-[15px] font-semibold text-[var(--color-heading)]">
          Where to go next
        </h2>
        <div className="grid gap-3 md:grid-cols-3">
          {NEXT_STEPS.map(({ to, label, description, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              className="hrms-card group flex items-start gap-3 p-4 transition-all hover:-translate-y-0.5 hover:border-[var(--color-primary)]/40 hover:shadow-[var(--shadow-popover)]"
            >
              <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-[var(--color-primary-soft)] text-[var(--color-primary)]">
                <Icon className="size-5" aria-hidden="true" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1 font-medium text-[var(--color-heading)] group-hover:text-[var(--color-primary)]">
                  {label}
                  <ArrowRight className="size-4 opacity-0 transition-opacity rtl-flip group-hover:opacity-100" aria-hidden="true" />
                </span>
                <span className="mt-0.5 block text-sm text-[var(--color-muted)]">{description}</span>
              </span>
            </Link>
          ))}
        </div>
      </section>
    </div>
  )
}
